import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { LockOutlined } from "@ant-design/icons";
import { useLazyQuery } from "@apollo/client";
import "./QueryPage.css";

import {
  searchExternalUsersInit,
  getAssociatedTalentsInit,
  searchSkillListingQueryInit,
  nlSearchExternalInit,
  nlSearchExternalClear,
  SearchValue,
  toggleSearchTrue,
  addBatchMatches,
  fetchUserProfile,
  fetchCreditsBalanceRequested,
} from "../../redux/actions";
import FeatureGate from "../FeatureGate/FeatureGate";
import { GIGS_QUERY } from "../../components/PlaceBidModal/gigsQuery";
import { ApolloWrapper } from "../../components/ApolloWrapper/ApolloWrapper";

import QueryPageHeader from "./components/organisms/QueryPageHeader";
import FilterDrawer from "./components/organisms/FilterDrawer";
import NlpSearchModal from "./components/organisms/NlpSearchModal";
import TalentSection from "./components/organisms/TalentSection";
import InternalTalentCard from "./components/organisms/InternalTalentCard";
import MarketplaceTalentCard from "./components/organisms/MarketplaceTalentCard";
import ExternalTalentCard from "./components/organisms/ExternalTalentCard";
import Pagination from "./components/organisms/Pagination";
import PlaceBidModal from "./components/organisms/PlaceBidModal";
import SelectJobModal from "./components/organisms/SelectJobModal";
import QueryEmptyState from "./components/organisms/QueryEmptyState";
import {
  ExternalTalentIcon,
  InternalTalentIcon,
  MarketplaceTalentIcon,
} from "./QueryPageIcons";
import { INITIAL, getActiveFilterCount } from "./components/organisms/FilterDrawerPanels/filterOptions";
import {
  buildExternalFiltersPayload,
  buildMarketplaceFiltersPayload,
  buildNonSkillRadarPmtFiltersPayload,
} from "./filterPayloadMappers";
import { buildQueryResultSearch, parseQueryResultState } from "./queryStateUrl";
import lockedExternalTalentPlaceholders from "./data/lockedExternalTalentPlaceholders.json";
import ManageSubscriptionModal from "../../components/ManageSubscriptionModal/ManageSubscriptionModal";
import BuyCreditsModal from "../../components/BuyCreditsModal/BuyCreditsModal";
import Loader from "../../components/Loading/Loader";
import { canUseSubscriptionFeatures, getStoredUserProfile } from "../../shared/billing";
import {
  EXTERNAL_TALENT_LOCK_ACTIONS,
  EXTERNAL_TALENT_LOCK_CONFIG,
  getCreditsBalancePayload,
  getExternalTalentLockState,
} from "./externalTalentLocking";

const PAGE_SIZE = 4;
const TOTAL_PAGES = 10;

// talentFilters as the AI endpoints hand them back (job_title / skills /
// location / years_of_experience) mapped onto our own filter shape.
const buildFiltersFromTalentFilters = (tf) => ({
  ...INITIAL,
  jobTitleInclude: Array.isArray(tf?.job_title) ? tf.job_title : [],
  location: Array.isArray(tf?.location)
    ? tf.location.filter((v) => typeof v === "string" && v.trim())
    : (typeof tf?.location === "string" && tf.location.trim() ? [tf.location] : []),
  skill: Array.isArray(tf?.skills) ? tf.skills : [],
  yearsOfExp: Array.isArray(tf?.years_of_experience)
    ? tf.years_of_experience.map((v) => (typeof v === "string" ? v.replace(/_/g, "-") : v))
    : [],
});

const safeLocalStorageGet = (key, fallback = "") => {
  try {
    return localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
};

const safeLocalStorageRemove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {}
};

const safeDecodeURIComponent = (value = "") => {
  try {
    return decodeURIComponent(value || "");
  } catch {
    return value || "";
  }
};

// Words that only turn up when someone is writing a sentence — pronouns,
// intent verbs and qualifying prepositions. Job titles do not contain these,
// so a single one is enough ("nurses in italy" vs "RN - Acute Care Med Surg").
const NL_SENTENCE_WORDS = [
  "i", "me", "my", "we", "our", "you", "please",
  "find", "need", "want", "looking", "look", "show", "give", "get", "hire",
  "search", "who", "whom", "whose", "someone", "anyone",
  "candidate", "candidates", "talent", "talents", "profile", "profiles",
  "in", "near", "around", "with", "without", "from", "having",
];

// These also show up in real job titles ("Director of Nursing", "Nurse for
// ICU"), so one on its own does not mean the entry is a sentence.
const NL_FILLER_WORDS = [
  "a", "an", "the", "and", "or", "for", "of", "at", "on", "to", "that",
  "year", "years", "yrs", "experience", "experienced", "has",
];

// Decides whether a typed query should go to /nl-search instead of the plain
// keyword search. Length is deliberately not used — job titles get long too
// ("RN - Acute Care Med Surg") and a wrong guess here costs search credits.
const isNaturalLanguageQuery = (value = "") => {
  const words = String(value)
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

  if (words.some((word) => NL_SENTENCE_WORDS.includes(word))) {
    return true;
  }

  return words.filter((word) => NL_FILLER_WORDS.includes(word)).length >= 2;
};

const QueryPage = () => {
  const { searchValue } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  // A fresh signup can reach this page before the profile (and its Source) is in
  // localStorage, which would read a Skill Radar user as a marketplace-only one.
  // Subscribing to the profile slice re-renders once it arrives, so the flag
  // below is re-read instead of staying stuck on the first mount's value.
  const reduxProfile = useSelector((state) => state?.profileReducer?.user?.profile);
  const authToken = useSelector(
    (state) => state?.authReducer?.user?.authToken
  );
  const usePmtTalentsSearch = Boolean(authToken);
  const gigsCalledRef = useRef(false);
  const subscriptionFeaturesEnabled =
    usePmtTalentsSearch && canUseSubscriptionFeatures();

  const fromSignup = new URLSearchParams(location.search).get("fromSignup") === "true";

  useEffect(() => {
    if (fromSignup) {
      dispatch(SearchValue(safeDecodeURIComponent(searchValue)));
      history.replace(`/external/${encodeURIComponent(searchValue || "")}`);
    }
  }, []);

  const _urlQuery = parseQueryResultState(location.search);
  const _savedQuery = (() => { try { const s = safeLocalStorageGet("queryPage_state", ""); return s ? JSON.parse(s) : null; } catch { return null; } })();
  // A Write with AI run leaves the filters it resolved in localStorage, and the
  // landing search below is built from them. Seed the filter state with the same
  // thing, otherwise the drawer opens empty and the next apply or page fetch
  // silently drops the job title, skills and experience it searched on.
  const _aiQueryFilters = (() => {
    const raw = safeLocalStorageGet("aiTalentFilters", "");
    if (!raw) return null;
    try {
      const tf = JSON.parse(raw);
      return tf ? buildFiltersFromTalentFilters(tf) : null;
    } catch {
      return null;
    }
  })();
    // Skill-radar users always end up on "last_active" (the sort control is
    // hidden for them). Resolving that here, instead of letting the effect below
  // flip it right after mount, stops the search effect from firing twice and
  // charging credits for a duplicate nl_search.
  const [sortOrder, setSortOrder] = useState(
    subscriptionFeaturesEnabled && _urlQuery.sortOrder === "relevant"
      ? "last_active"
      : _urlQuery.sortOrder || _savedQuery?.sortOrder || "relevant"
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const [nlpModalOpen, setNlpModalOpen] = useState(false);
  const [nlpModalKey, setNlpModalKey] = useState(0);
  // "aiTalentFilters" is only ever written by a Write with AI run, so its
  // presence means the description we land with came from there and must stay on
  // the plain search endpoint instead of charging an nl_search.
  const [nlpState, setNlpState] = useState({
    description: safeLocalStorageGet("nlpDescription", ''),
    talentFilters: null,
    jobTitles: [],
    aiGenerated: Boolean(safeLocalStorageGet("aiTalentFilters", "")),
  });
  const _initialFilters = _urlQuery.hasAppliedFilters
    ? _urlQuery.filters
    : (_aiQueryFilters || _savedQuery?.filters || INITIAL);
  const [filters, setFilters] = useState(_initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(_initialFilters);
  const [currentPage, setCurrentPage] = useState(_urlQuery.hasAppliedFilters || _urlQuery.currentPage > 1 ? (_urlQuery.currentPage || 1) : (_savedQuery?.currentPage || 1));
  const [activeTab, setActiveTab] = useState("external");
  const [bidModal, setBidModal] = useState({
    open: false,
    talent: null,
    talentType: null,
  });
  const [showManageSubscriptionModal, setShowManageSubscriptionModal] = useState(false);
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  const [selectedExternalIds, setSelectedExternalIds] = useState([]);
  const [selectedMarketplaceIds, setSelectedMarketplaceIds] = useState([]);
  const [marketplaceMatchesOpen, setMarketplaceMatchesOpen] = useState(false);
  const hasHandledInitialUrlState = useRef(false);
  const lastUrlHadFilterStateRef = useRef(false);
  const nlFiltersSyncedRef = useRef("");

  useEffect(() => {
    if (subscriptionFeaturesEnabled && sortOrder === "relevant") {
      handleSortOrderChange("last_active");
    }
  }, [subscriptionFeaturesEnabled, sortOrder]);

  useEffect(() => {
    try {
      localStorage.setItem("queryPage_state", JSON.stringify({ sortOrder, filters, currentPage }));
    } catch {}
  }, [sortOrder, filters, currentPage, activeTab]);

  useEffect(() => {
    const nextQuery = parseQueryResultState(location.search);
    const hasUrlState = nextQuery.hasAppliedFilters || nextQuery.currentPage > 1 || nextQuery.sortOrder !== "relevant";

    if (!hasHandledInitialUrlState.current) {
      hasHandledInitialUrlState.current = true;
      lastUrlHadFilterStateRef.current = hasUrlState;
      if (!hasUrlState) return;
    }

    // Only take the sort order from the URL when it is actually in the URL. Unrelated pushes
    // (e.g. SearchBar appending ?searchType=...) would otherwise reset it to "relevant" and
    // ping-pong against the subscription default, firing extra searches.
    if (new URLSearchParams(location.search).has("sort")) {
      setSortOrder(nextQuery.sortOrder);
    }

    // The same unrelated pushes must not wipe the filters either. Only clear
    // them when the URL actually dropped state it used to carry (a real back
    // navigation out of a filtered result); a push that never had state in the
    // first place leaves the current filters alone.
    const previousUrlHadFilterState = lastUrlHadFilterStateRef.current;
    lastUrlHadFilterStateRef.current = hasUrlState;

    if (hasUrlState) {
      setFilters(nextQuery.filters);
      setAppliedFilters(nextQuery.filters);
      setCurrentPage(nextQuery.currentPage);
    } else if (previousUrlHadFilterState) {
      setFilters(INITIAL);
      setAppliedFilters(INITIAL);
      setCurrentPage(1);
    }
  }, [location.search]);

  const searchTerm = searchValue
    ? safeDecodeURIComponent(searchValue)
    : "RN Med Surg";

  const syncQueryParams = ({
    nextFilters = appliedFilters,
    nextSortOrder = sortOrder,
    nextPage = currentPage,
    replace = false,
  } = {}) => {
    let nextSearch = "";

    try {
      nextSearch = buildQueryResultSearch({
        filters: nextFilters,
        sortOrder: nextSortOrder,
        currentPage: nextPage,
      });
    } catch {
      nextSearch = "";
    }

    if (nextSearch === location.search) return;

    const method = replace ? history.replace : history.push;
    try {
      method({
        pathname: location.pathname,
        search: nextSearch,
      });
    } catch {}
  };

  const handleSortOrderChange = (nextSortOrder) => {
    setSortOrder(nextSortOrder);
    setCurrentPage(1);
    syncQueryParams({ nextSortOrder, nextPage: 1 });
  };

  const handlePageChange = (page) => {
    const safePage = Number.isInteger(Number(page)) && Number(page) > 0 ? Number(page) : 1;
    setCurrentPage(safePage);
    syncQueryParams({ nextPage: safePage });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Redux state
  const {
    data: externalTalents,
    loading: externalLoading,
    totalCount: externalTotalCount,
  } = useSelector((state) => state.searchExternalUsersReducer);
  const safeExternalTalents = Array.isArray(externalTalents)
    ? externalTalents
    : [];

  const {
    data: internalTalents,
    loading: internalLoading,
    totalCount: internalTotalCount,
  } = useSelector((state) => state.associatedTalentsReducer);
  const safeInternalTalents = Array.isArray(internalTalents)
    ? internalTalents
    : [];
  const {
    data: marketplaceTalents,
    loading: marketplaceLoading,
    totalCount: marketplaceTotalCount,
  } = useSelector((state) => state.queryPageSkillListingReducer);
  const safeMarketplaceTalents = Array.isArray(marketplaceTalents)
    ? marketplaceTalents
    : [];
  const {
    creditsBalance,
    creditsBalanceLoaded,
    plans,
    loading: plansLoading,
  } = useSelector((state) => state.plansReducer);

  const creditsBalancePayload = getCreditsBalancePayload(creditsBalance);
  const availableCredits = Number(creditsBalancePayload?.totalCreditsBalance) || 0;

  // Only refetch when the stored profile has no Source to read at all — the
  // state a fresh signup can land in. A resolved profile, Skill Radar or not,
  // skips the call so this costs nothing on a normal search.
  useEffect(() => {
    if (!authToken) return;
    const storedProfile = getStoredUserProfile();
    if (storedProfile?.Source || storedProfile?.profile?.Source) return;
    dispatch(fetchUserProfile());
  }, [authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (subscriptionFeaturesEnabled && !creditsBalanceLoaded) {
      dispatch(fetchCreditsBalanceRequested());
    }
  }, [subscriptionFeaturesEnabled, creditsBalanceLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Same gigs call the /external results page makes, so landing on /query
  // hits it too. Fires once, as soon as the profile carries a ProfileId.
  const [getGigs] = useLazyQuery(GIGS_QUERY, { fetchPolicy: "no-cache" });

  useEffect(() => {
    const profileId = reduxProfile?.ProfileId;
    if (!authToken || !profileId || gigsCalledRef.current) return;
    gigsCalledRef.current = true;
    getGigs({
      variables: {
        MygigsProfileId: profileId,
        all: "",
        isPrivateGig: false,
        _page: "1",
        _perPage: 21,
        _sort: "MOST_RECENT",
        JobOrderStatusName: "All",
      },
    });
  }, [authToken, reduxProfile?.ProfileId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Dispatch all 3 API calls on mount / when searchTerm changes

  
  const {
    isActive: isAiMode,
    data: nlExternalTalents,
    loading: nlExternalLoading,
    totalCount: nlExternalTotalCount,
    errorType: nlErrorType,
    appliedFilters: nlAppliedFilters,
  } = useSelector((state) => state.nlSearchExternalReducer);

  const isNaturalLanguageSearchTerm = isNaturalLanguageQuery(searchTerm);
  const hasNlpRefinement =
    Boolean(nlpState.description) || (nlpState.jobTitles?.length || 0) > 0;
  // A "Write with AI" run stays on the plain search endpoint, so paging,
  // sorting and filtering after it must not fall back to nl_search either.
  const useNlSearch =
    !nlpState.aiGenerated && (isNaturalLanguageSearchTerm || hasNlpRefinement);

  const isMarketplaceOnlySearch = !subscriptionFeaturesEnabled;
  const displayedMarketplaceTalents = safeMarketplaceTalents;
  const displayedMarketplaceLoading = marketplaceLoading;

  const visibleMarketplaceIds = isMarketplaceOnlySearch
    ? displayedMarketplaceTalents.map((talent) => talent.id || talent._id)
    : [];
  const allMarketplaceSelected =
    visibleMarketplaceIds.length > 0 &&
    visibleMarketplaceIds.every((id) => selectedMarketplaceIds.includes(id));

  const handleToggleMarketplaceSelect = (id) => {
    setSelectedMarketplaceIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAllMarketplace = () => {
    setSelectedMarketplaceIds((prev) => {
      if (allMarketplaceSelected) {
        return prev.filter((id) => !visibleMarketplaceIds.includes(id));
      }

      return [...new Set([...prev, ...visibleMarketplaceIds])];
    });
  };

  const handleClearSelectedMarketplace = () => setSelectedMarketplaceIds([]);

  const handleOpenBulkBidModalMarketplace = () => {
    setBidModal({
      open: true,
      talent: { name: "", title: "", avatar: null, initials: "" },
      talentType: "internal",
      skillListingId: null,
      skillListingIds: selectedMarketplaceIds,
    });
  };

  const handleOpenMarketplaceMatchesModal = () => {
    setMarketplaceMatchesOpen(true);
  };

  const handleCloseMarketplaceMatchesModal = () => {
    setMarketplaceMatchesOpen(false);
  };

  const handleSelectMarketplaceJob = (job) => {
    dispatch(
      addBatchMatches({
        GigId: job.JobOrderId,
        JobTitle: job.title,
        SkillListingIds: selectedMarketplaceIds,
      })
    );
    setMarketplaceMatchesOpen(false);
    setSelectedMarketplaceIds([]);
  };

  const externalTalentLockState = subscriptionFeaturesEnabled ? getExternalTalentLockState({
    isAiMode,
    creditsBalanceLoaded,
    creditsBalancePayload,
    availableCredits,
  }) || (nlErrorType === 'BADREQUEST' ? 'creditsEnded' : null) : null;
  const externalTalentLocked = Boolean(externalTalentLockState);
  const externalTalentLockConfig =
    EXTERNAL_TALENT_LOCK_CONFIG[externalTalentLockState] || {};
  const handleExternalTalentLockedAction = () => {
    if (!subscriptionFeaturesEnabled) return;

    if (
      externalTalentLockConfig.actionType ===
      EXTERNAL_TALENT_LOCK_ACTIONS.BUY_CREDITS
    ) {
      setShowBuyCreditsModal(true);
      return;
    }

    setShowManageSubscriptionModal(true);
  };

  const displayedExternalTalents = externalTalentLocked
    ? lockedExternalTalentPlaceholders
    : safeExternalTalents;

  const displayedExternalTalentsAi = isAiMode
    ? (externalTalentLocked ? lockedExternalTalentPlaceholders : (nlExternalTalents || []))
    : null;
  const displayedExternalLoading = isAiMode ? nlExternalLoading : externalLoading;

  const currentExternalTalents = isAiMode ? (displayedExternalTalentsAi || []) : displayedExternalTalents;
  const visibleExternalIds = externalTalentLocked
    ? []
    : currentExternalTalents.slice(0, PAGE_SIZE).map((talent) => talent.id || talent._id);
  const allExternalSelected =
    visibleExternalIds.length > 0 &&
    visibleExternalIds.every((id) => selectedExternalIds.includes(id));

  const handleToggleExternalSelect = (id) => {
    setSelectedExternalIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAllExternal = () => {
    setSelectedExternalIds((prev) => {
      if (allExternalSelected) {
        return prev.filter((id) => !visibleExternalIds.includes(id));
      }

      return [...new Set([...prev, ...visibleExternalIds])];
    });
  };

  const handleClearSelectedExternal = () => setSelectedExternalIds([]);

  const handleOpenBulkBidModal = () => {
    setBidModal({
      open: true,
      talent: { name: "", title: "", avatar: null, initials: "" },
      talentType: "external",
      skillListingId: null,
      skillListingIds: selectedExternalIds,
    });
  };

  useEffect(() => {
    setSelectedExternalIds([]);
    setSelectedMarketplaceIds([]);
  }, [currentPage, sortOrder, appliedFilters, activeTab, searchTerm]);

  // External/NL searches are credit-charged, so never let the same request go out twice.
  const lastSearchSignatureRef = useRef("");

  useEffect(() => {
    const signature = JSON.stringify({
      searchTerm,
      currentPage,
      sortOrder,
      isAiMode,
      isMarketplaceOnlySearch,
      usePmtTalentsSearch,
      appliedFilters,
    });
    if (lastSearchSignatureRef.current === signature) return;
    lastSearchSignatureRef.current = signature;

    const payload = {
      search: searchTerm,
      page: currentPage,
      perPage: PAGE_SIZE,
    };

    if (isMarketplaceOnlySearch) {
      dispatch(
        searchSkillListingQueryInit({
          search: searchTerm,
          perPage: 25,
          usePmtTalentsSearch,
          ...buildNonSkillRadarPmtFiltersPayload(appliedFilters, currentPage, sortOrder),
        })
      );
      return;
    }

    const associatedTalentPayload = {
      ...payload,
      pageSize: PAGE_SIZE,
      ...(useNlSearch && { aiSearch: true }),
    };

    dispatch(getAssociatedTalentsInit(associatedTalentPayload));

    if (useNlSearch) {
      const aiFilterPayload = getActiveFilterCount(appliedFilters) > 0
        ? buildExternalFiltersPayload(appliedFilters, currentPage)
        : {};
      const nlpJobLocation = safeLocalStorageGet("nlpJobLocation", "");
      const baseNlQuery = nlpState.description || searchTerm;
      const nlQuery = (nlpJobLocation && !aiFilterPayload.location?.length)
        ? `${baseNlQuery} and in ${nlpJobLocation.split(",")[0]}`
        : baseNlQuery;
      if (!aiFilterPayload.location?.length && nlpJobLocation) {
        aiFilterPayload.location = [nlpJobLocation];
        aiFilterPayload.location_radius = 50;
      }
      dispatch(nlSearchExternalInit({
        query: nlQuery,
        page: currentPage,
        ...aiFilterPayload,
      }));
      dispatch(
        searchSkillListingQueryInit({
          ...payload,
          aiSearch: true,
          usePmtTalentsSearch,
          skillName: searchTerm,
          ...buildMarketplaceFiltersPayload(appliedFilters, currentPage, sortOrder),
        })
      );
    } else {
      // Drop any stale AI results so the UI renders the keyword search instead.
      if (isAiMode) {
        dispatch(nlSearchExternalClear());
      }
      const rawAiFilters = safeLocalStorageGet("aiTalentFilters", "");
      if (rawAiFilters) {
        try {
          const aiFilters = buildFiltersFromTalentFilters(JSON.parse(rawAiFilters));
          const filterPayload = buildExternalFiltersPayload(aiFilters, currentPage);
          dispatch(searchExternalUsersInit({ search: searchTerm, ...filterPayload }));
        } catch {
          dispatch(searchExternalUsersInit({ ...payload, perPage: 25 }));
        }
      } else if (getActiveFilterCount(appliedFilters) > 0) {
        const filterPayload = buildExternalFiltersPayload(appliedFilters, currentPage);
        dispatch(searchExternalUsersInit({ search: searchTerm, perPage: 25, ...filterPayload }));
      } else {
        dispatch(searchExternalUsersInit({ ...payload, perPage: 25 }));
      }
      dispatch(
        searchSkillListingQueryInit({
          ...payload,
          usePmtTalentsSearch,
          skillName: searchTerm,
          ...buildMarketplaceFiltersPayload(appliedFilters, currentPage, sortOrder),
        })
      );
    }
  }, [searchTerm, currentPage, sortOrder, isMarketplaceOnlySearch, usePmtTalentsSearch, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // nl_search resolves its own filters from the prompt (job title, skills,
  // location, experience). Mirror all of them onto the filter state, not just
  // the skills, so the drawer shows what was actually searched and the next
  // apply or page fetch keeps sending it. Anything the user already set wins.
  useEffect(() => {
    const pickList = (value) =>
      Array.isArray(value)
        ? value.filter((item) => typeof item === "string" && item.trim())
        : [];
    const nlFilters = {
      jobTitleInclude: pickList(nlAppliedFilters?.job_title),
      skill: pickList(nlAppliedFilters?.skills),
      location: pickList(nlAppliedFilters?.location),
      yearsOfExp: pickList(nlAppliedFilters?.years_of_experience).map((value) =>
        value.replace(/_/g, "-")
      ),
    };
    const nlRadius = Number(nlAppliedFilters?.location_radius);
    if (!Object.values(nlFilters).some((value) => value.length)) {
      nlFiltersSyncedRef.current = "";
      return;
    }
    const signature = JSON.stringify([nlFilters, nlRadius]);
    if (nlFiltersSyncedRef.current === signature) return;
    nlFiltersSyncedRef.current = signature;
    const mergeNlFilters = (prev) => {
      const next = { ...prev };
      Object.entries(nlFilters).forEach(([key, value]) => {
        if (value.length && !next[key]?.length) next[key] = value;
      });
      if (next.location?.length && !next.locationRadius && Number.isFinite(nlRadius)) {
        next.locationRadius = nlRadius;
      }
      return next;
    };
    setFilters(mergeNlFilters);
    setAppliedFilters(mergeNlFilters);
  }, [nlAppliedFilters]);

  const internalCount = internalTotalCount || safeInternalTalents.length;
  const marketplaceCountRaw = marketplaceTotalCount || safeMarketplaceTalents.length;
  const marketplaceCount = marketplaceCountRaw;

  const externalCount = isAiMode
    ? nlExternalTotalCount || (nlExternalTalents?.length ?? 0)
    : externalTotalCount || safeExternalTalents.length;
  // Header count sums every source that actually ran: internal, marketplace
  // and external (keyword or nl-search). The narrower branches below only
  // fetch one of them, so they stay single-source.
  const totalMatches = isMarketplaceOnlySearch
    ? marketplaceCount
    : fromSignup
    ? externalCount
    : internalCount +
      marketplaceCount +
      (externalTalentLocked ? 0 : externalCount);

  useEffect(() => {
    const isSearchDomain = (p) => p.startsWith('/external/') || p === '/external' || p.startsWith('/query/') || p === '/query';
    return () => {
      if (!isSearchDomain(history.location.pathname)) {
        try {
          safeLocalStorageRemove("queryPage_state");
          safeLocalStorageRemove("aiTalentFilters");
          safeLocalStorageRemove("nlpDescription");
          safeLocalStorageRemove("nlpJobLocation");
        } catch (e) {}
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isFirstSearchTermRender = useRef(true);
  useEffect(() => {
    if (isFirstSearchTermRender.current) {
      isFirstSearchTermRender.current = false;
      return;
    }
    setActiveTab("external");
    dispatch(SearchValue(searchTerm));
    setNlpState({ description: '', talentFilters: null, jobTitles: [] });
    setCurrentPage(1);
    safeLocalStorageRemove("aiTalentFilters");
    safeLocalStorageRemove("nlpDescription");
    safeLocalStorageRemove("nlpJobLocation");
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isMarketplaceOnlySearch) {
      setActiveTab("pmt");
      return;
    }

    if (!displayedExternalLoading && !marketplaceLoading) {
      if ((externalCount === 0 && !externalTalentLocked && marketplaceCount > 0 && getActiveFilterCount(filters) === 0) ||
          (externalTalentLocked && marketplaceCount > 0)) {
        setActiveTab("pmt");
      }
    }
  }, [displayedExternalLoading, marketplaceLoading, externalCount, marketplaceCount, externalTalentLocked, filters, isMarketplaceOnlySearch]);

  const handleViewInternalProfile = (id) => {
    const profilePath = `/internal-detail/${id}`;
    if (!authToken) {
      localStorage.setItem("redirectUrl", profilePath);
      history.push("/login");
      return;
    }
    history.push(profilePath);
  };

  const handleViewExternalProfile = (id) => {
    const profilePath = `/external-detail/${id}`;
    if (!authToken) {
      localStorage.setItem("redirectUrl", profilePath);
      history.push("/login");
      return;
    }
    history.push(profilePath);
  };

  const handlePlaceBid = (talent, talentType) => {
    console.log("talent", talent);
    setBidModal({
      open: true,
      talent,
      talentType,
      skillListingId:
        talent?.id ?? talent?._id ?? talent?.SkillListingId ?? null,
    });
  };

  const encodedSearchTerm = encodeURIComponent(searchTerm);

  const showExternalTab = externalCount > 0 || externalTalentLocked;
  const showPmtTab = !fromSignup && marketplaceCount > 0;
  const showTabs = !fromSignup && (showExternalTab || showPmtTab) && internalCount > 0 && !displayedExternalLoading && !marketplaceLoading;

  const fetchWithFilters = (nextFilters, page = 1) => {
    if (isMarketplaceOnlySearch) {
      dispatch(
        searchSkillListingQueryInit({
          search: searchTerm,
          perPage: 25,
          usePmtTalentsSearch,
          ...buildNonSkillRadarPmtFiltersPayload(nextFilters, page, sortOrder),
        })
      );
      return;
    }

    const filterPayload = buildExternalFiltersPayload(nextFilters, page);
    if (useNlSearch) {
      const nlpJobLocation = safeLocalStorageGet("nlpJobLocation", "");
      const baseNlQuery = nlpState.description || searchTerm;
      const nlQuery = (nlpJobLocation && !filterPayload.location?.length)
        ? `${baseNlQuery} and in ${nlpJobLocation.split(",")[0]}`
        : baseNlQuery;
      if (!filterPayload.location?.length && nlpJobLocation) {
        filterPayload.location = [nlpJobLocation];
        filterPayload.location_radius = 50;
      }
      dispatch(nlSearchExternalInit({ query: nlQuery, page, ...filterPayload }));
    } else {
      dispatch(searchExternalUsersInit({ search: searchTerm, perPage: 25, page, ...filterPayload }));
    }
  };

  const handleNlpSearch = ({ jobTitles, description, talentFilters, aiGenerated }) => {
    setNlpState({ description, talentFilters, jobTitles: jobTitles || [], aiGenerated });
    // A "Write with AI" description goes to the plain search endpoint with the
    // very same payload nl_search used to receive.
    const runSearch = aiGenerated ? searchExternalUsersInit : nlSearchExternalInit;
    if (aiGenerated) {
      dispatch(nlSearchExternalClear());
      // write-with-ai already returned the filters it resolved, so apply those
      // instead of waiting on an nl-search response that will never come.
      if (talentFilters) {
        const aiFilters = buildFiltersFromTalentFilters(talentFilters);
        setFilters(aiFilters);
        setAppliedFilters(aiFilters);
        setCurrentPage(1);
        syncQueryParams({ nextFilters: aiFilters, nextPage: 1 });
        const titles = aiFilters.jobTitleInclude;
        if (titles.length > 0) dispatch(SearchValue(titles.join(', ')));
        dispatch(
          searchExternalUsersInit({
            query: description || titles.join(', '),
            perPage: 25,
            ...buildExternalFiltersPayload(aiFilters, 1),
          })
        );
        return;
      }
    }
    if (jobTitles?.length > 0) {
      const nextFilters = { ...filters, jobTitleInclude: jobTitles };
      setFilters(nextFilters);
      setAppliedFilters(nextFilters);
      dispatch(SearchValue(jobTitles.join(', ') || description));
      dispatch(runSearch({ query: description || jobTitles.join(', '), page: 1, job_title: jobTitles }));
    } else if (description) {
      dispatch(runSearch({ query: description, page: 1 }));
    }
  };

  const handleOpenFilter = () => {
    const draft = { ...appliedFilters };
    if (isAiMode && !draft.location?.length) {
      const nlpJobLocation = localStorage.getItem("nlpJobLocation");
      if (nlpJobLocation) draft.location = [nlpJobLocation];
    }
    setFilters(draft);
    setFilterOpen(true);
  };
  const handleCloseFilter = () => setFilterOpen(false);
  const handleResetFilter = () => {
    safeLocalStorageRemove("aiTalentFilters");
    safeLocalStorageRemove("nlpDescription");
    safeLocalStorageRemove("nlpJobLocation");
    setFilters(INITIAL);
    setAppliedFilters(INITIAL);
    setFilterOpen(false);
    setCurrentPage(1);
    syncQueryParams({ nextFilters: INITIAL, nextPage: 1 });
    setNlpState({ description: '', talentFilters: null, jobTitles: [] });
    setNlpModalKey((k) => k + 1);
    dispatch(toggleSearchTrue());
    dispatch(nlSearchExternalClear());
    if (isMarketplaceOnlySearch) {
      dispatch(
        searchSkillListingQueryInit({
          search: searchTerm,
          perPage: 25,
          usePmtTalentsSearch,
          ...buildNonSkillRadarPmtFiltersPayload(INITIAL, 1, sortOrder),
        })
      );
    } else if (isNaturalLanguageSearchTerm) {
      dispatch(nlSearchExternalInit({ query: searchTerm, page: 1 }));
    } else {
      dispatch(searchExternalUsersInit({ search: searchTerm, perPage: 25, page: 1 }));
    }
  };
  const handleApplyFilter = (next) => {
    const applied = next || INITIAL;
    setFilters(applied);
    setAppliedFilters(applied);
    setFilterOpen(false);
    setCurrentPage(1);
    syncQueryParams({ nextFilters: applied, nextPage: 1 });
    const appliedTitles = applied?.jobTitleInclude || [];
    setNlpState((prev) => ({ ...prev, jobTitles: appliedTitles }));
    if (appliedTitles.length > 0) {
      dispatch(SearchValue(appliedTitles.join(', ')));
    }
    setNlpModalKey((k) => k + 1);
    fetchWithFilters(applied, 1);
  };

  const planStatus = creditsBalance?.data?.status || '';
  const isExpiredPlan = subscriptionFeaturesEnabled && (planStatus === 'expired' || nlErrorType === 'FORBIDDEN');

  if (subscriptionFeaturesEnabled && !creditsBalanceLoaded) return <Loader />;
  if (isExpiredPlan && marketplaceLoading) return <Loader />;
  if (!isExpiredPlan && (isMarketplaceOnlySearch ? marketplaceLoading : (displayedExternalLoading || marketplaceLoading || internalLoading))) return <Loader />;

  return (
    <div className="qp-container">
      {(!isExpiredPlan || marketplaceCount > 0) && (
        <QueryPageHeader
          totalMatches={isExpiredPlan ? marketplaceCount : totalMatches}
          searchTerm={nlpState.jobTitles?.length ? nlpState.jobTitles.join(', ') : searchTerm}
          sortOrder={sortOrder}
          onChangeSortOrder={subscriptionFeaturesEnabled ? undefined : handleSortOrderChange}
          showMostRelevantSort={!subscriptionFeaturesEnabled}
          onOpenFilter={(!fromSignup && !isExpiredPlan) ? handleOpenFilter : undefined}
          filterCount={getActiveFilterCount(appliedFilters) + (subscriptionFeaturesEnabled && nlpState.description ? 1 : 0)}
          showSelectAll={
            !subscriptionFeaturesEnabled && (
              isMarketplaceOnlySearch
                ? visibleMarketplaceIds.length > 0
                : (!externalTalentLocked && !isExpiredPlan && visibleExternalIds.length > 0)
            )
          }
          allSelected={isMarketplaceOnlySearch ? allMarketplaceSelected : allExternalSelected}
          onToggleSelectAll={isMarketplaceOnlySearch ? handleToggleSelectAllMarketplace : handleToggleSelectAllExternal}
          showActions={Boolean(authToken)}
        />
      )}

      {authToken && !isMarketplaceOnlySearch && selectedExternalIds.length > 0 && (
        <div className="qp-bulk-bar">
          <button className="qp-bulk-btn qp-bulk-btn-cancel" type="button"
            onClick={handleClearSelectedExternal}>
            Cancel
          </button>
          <button className="qp-bulk-btn qp-bulk-btn-primary" type="button"
            onClick={handleOpenBulkBidModal}>
            Place a bid ( {selectedExternalIds.length} Skill listings )
          </button>
        </div>
      )}

      {authToken && isMarketplaceOnlySearch && selectedMarketplaceIds.length > 0 && (
        <div className="qp-bulk-bar">
          <button className="qp-bulk-btn qp-bulk-btn-cancel" type="button"
            onClick={handleClearSelectedMarketplace}>
            Cancel
          </button>
          <button className="qp-bulk-btn" type="button" onClick={handleOpenMarketplaceMatchesModal}>
            Add to matches ( {selectedMarketplaceIds.length} Skill listings )
          </button>
          <button className="qp-bulk-btn qp-bulk-btn-primary" type="button"
            onClick={handleOpenBulkBidModalMarketplace}>
            Place a bid ( {selectedMarketplaceIds.length} Skill listings )
          </button>
        </div>
      )}
      {subscriptionFeaturesEnabled && (
        <NlpSearchModal
          key={nlpModalKey}
          open={nlpModalOpen}
          onClose={() => setNlpModalOpen(false)}
          onSearch={handleNlpSearch}
          hideTitleField={nlpState.jobTitles?.length > 0 || !!nlpState.description}
          initialJobTitles={nlpState.jobTitles || []}
          initialDescription={nlpState.description || ''}
        />
      )}

      <FilterDrawer
        open={filterOpen}
        values={filters}
        onChange={setFilters}
        onClose={handleCloseFilter}
        onReset={handleResetFilter}
        onApply={handleApplyFilter}
        onNlpClick={subscriptionFeaturesEnabled ? () => setNlpModalOpen(true) : undefined}
        nlpState={subscriptionFeaturesEnabled ? nlpState : undefined}
        onNlpEdit={subscriptionFeaturesEnabled ? () => setNlpModalOpen(true) : undefined}
        onNlpClear={subscriptionFeaturesEnabled ? () => {
          setNlpState((prev) => ({ ...prev, description: '' }));
          setNlpModalKey((k) => k + 1);
        } : undefined}
      />


      {isMarketplaceOnlySearch && totalMatches === 0 && !displayedMarketplaceLoading && (
        <QueryEmptyState
          searchQuery={searchTerm}
          onAction={getActiveFilterCount(appliedFilters) > 0 ? handleResetFilter : undefined}
          actionLabel="Reset Filter"
        />
      )}

      {isMarketplaceOnlySearch && marketplaceCount > 0 && (
        <div className="qp-section-body">
          {displayedMarketplaceLoading ? (
            <div className="qp-section-loader"><Loader backgroundColor="transparent" /></div>
          ) : (
            <div className="qp-cards-grid">
              {(!displayedMarketplaceTalents || displayedMarketplaceTalents.length === 0) ? (
                <QueryEmptyState searchQuery={searchTerm} talentType="market place" />
              ) : (
                displayedMarketplaceTalents.map((talent) => (
                  <InternalTalentCard
                    key={talent._id || talent.id}
                    talent={talent}
                    profileUrl={authToken ? `/internal-detail/${talent.id || talent._id}` : undefined}
                    onViewProfile={handleViewInternalProfile}
                    onPlaceBid={handlePlaceBid}
                    selectable={Boolean(authToken) && !subscriptionFeaturesEnabled}
                    selected={selectedMarketplaceIds.includes(talent.id || talent._id)}
                    onToggleSelect={handleToggleMarketplaceSelect}
                  />
                ))
              )}
            </div>
          )}
          {marketplaceCount > 25 && (
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(marketplaceCount / 25)}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      )}

      {isExpiredPlan && (
        <FeatureGate
          featureName="Skill Radar"
          iconType="search"
          description="Your free trial has ended. Choose a plan to continue accessing 850M+ professional profiles and keep finding the right talent for your open roles."
          bottomText="Skill Radar is a paid feature and was given to you for free during your trial period."
        />
      )}

      {!isMarketplaceOnlySearch && !isExpiredPlan && (
        <TalentSection
          iconEmoji={<ExternalTalentIcon />}
          iconClass="qp-icon-external"
          titleText="Skillradar Talent"
          count={externalTalentLocked ? 0 : externalCount}
          loading={displayedExternalLoading}
          showViewAll={!externalTalentLocked && externalCount > PAGE_SIZE}
          onViewAll={() => history.push(`/external/${encodedSearchTerm}`)}
          locked={externalTalentLocked}
          lockedTitle={externalTalentLockConfig.title}
          lockedDescription={externalTalentLockConfig.description}
          lockedActionLabel={externalTalentLockConfig.actionLabel}
          onLockedAction={handleExternalTalentLockedAction}
        >
          {currentExternalTalents.length === 0 ? (
            <QueryEmptyState searchQuery={searchTerm} talentType="external" />
          ) : (
            currentExternalTalents.slice(0, PAGE_SIZE).map((talent) => (
              <ExternalTalentCard
                key={talent._id || talent.id}
                talent={talent}
                profileUrl={authToken ? `/external-detail/${talent.id || talent._id}` : undefined}
                onViewProfile={handleViewExternalProfile}
                selectable={Boolean(authToken) && !subscriptionFeaturesEnabled && !externalTalentLocked}
                selected={selectedExternalIds.includes(talent.id || talent._id)}
                onToggleSelect={handleToggleExternalSelect}
              />
            ))
          )}
        </TalentSection>
      )}


      {!isMarketplaceOnlySearch && !fromSignup && (internalLoading || safeInternalTalents.length > 0) && (
        <TalentSection
          iconEmoji={<InternalTalentIcon />}
          iconClass="qp-icon-internal"
          titleText="Internal Talent"
          count={internalCount}
          loading={internalLoading}
          showViewAll={internalCount > PAGE_SIZE}
          onViewAll={() => history.push(`/internal/${encodedSearchTerm}`)}
        >
          {safeInternalTalents.slice(0, PAGE_SIZE).map((talent) => (
            <InternalTalentCard
              key={talent._id || talent.id}
              talent={talent}
              profileUrl={authToken ? `/internal-detail/${talent.id || talent._id}` : undefined}
              onViewProfile={handleViewInternalProfile}
              onPlaceBid={handlePlaceBid}
            />
          ))}
        </TalentSection>
      )}

      {/* No rows, no section — an empty PMT search must not flash a loader for
          a section that is about to disappear. */}
      {!isMarketplaceOnlySearch &&
        !fromSignup &&
        displayedMarketplaceTalents.length > 0 && (
        <TalentSection
          iconEmoji={<MarketplaceTalentIcon />}
          iconClass="qp-icon-marketplace"
          titleText="Skillgigs Marketplace Talent"
          count={marketplaceCount}
          loading={displayedMarketplaceLoading}
          showViewAll={marketplaceCount > PAGE_SIZE}
          onViewAll={() => history.push(`/marketplace/${encodedSearchTerm}`)}
        >
          {(!displayedMarketplaceTalents || displayedMarketplaceTalents.length === 0) ? (
            <QueryEmptyState searchQuery={searchTerm} talentType="market place" />
          ) : (
            displayedMarketplaceTalents.slice(0, PAGE_SIZE).map((talent) => (
              isMarketplaceOnlySearch ? (
                <InternalTalentCard
                  key={talent._id || talent.id}
                  talent={talent}
                  profileUrl={authToken ? `/internal-detail/${talent.id || talent._id}` : undefined}
                  onViewProfile={handleViewInternalProfile}
                  onPlaceBid={handlePlaceBid}
                  selectable={Boolean(authToken) && !subscriptionFeaturesEnabled}
                  selected={selectedMarketplaceIds.includes(talent.id || talent._id)}
                  onToggleSelect={handleToggleMarketplaceSelect}
                />
              ) : (
                <MarketplaceTalentCard
                  key={talent._id || talent.id}
                  talent={talent}
                  profileUrl={authToken ? `/internal-detail/${talent.id || talent._id}` : undefined}
                  onViewProfile={handleViewInternalProfile}
                  onPlaceBid={handlePlaceBid}
                />
              )
            ))
          )}
        </TalentSection>
      )}
      {subscriptionFeaturesEnabled && <BuyCreditsModal
        visible={showBuyCreditsModal}
        onClose={() => setShowBuyCreditsModal(false)}
        onUpgradeToPlan={() => setShowManageSubscriptionModal(true)}
        availableCredits={availableCredits}
      />}
      {subscriptionFeaturesEnabled && <ManageSubscriptionModal
        visible={showManageSubscriptionModal}
        onClose={() => setShowManageSubscriptionModal(false)}
        plans={plans}
        creditsBalance={creditsBalance}
        loading={plansLoading}
      />}
      {isMarketplaceOnlySearch && (
        <SelectJobModal
          open={marketplaceMatchesOpen}
          onClose={handleCloseMarketplaceMatchesModal}
          onSelect={handleSelectMarketplaceJob}
          showVmsFacilityFilter={false}
        />
      )}
      {bidModal.open ? (
        <PlaceBidModal
          open={bidModal.open}
          talent={bidModal.talent}
          talentType={bidModal.talentType}
          skillListingId={bidModal.skillListingId}
          skillListingIds={bidModal.skillListingIds}
          onClose={() => {
            if (bidModal.skillListingIds?.length) {
              setSelectedExternalIds([]);
              setSelectedMarketplaceIds([]);
            }
            setBidModal({
              open: false,
              talent: null,
              talentType: null,
              skillListingId: null,
              skillListingIds: null,
            });
          }}
        />
      ) : null}
      {/* <Pagination
        currentPage={currentPage}
        totalPages={TOTAL_PAGES}
        onPageChange={(page) => {
          setCurrentPage(page);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      /> */}
    </div>
  );
};

const QueryPageWithApollo = (props) => (
  <ApolloWrapper>
    <QueryPage {...props} />
  </ApolloWrapper>
);

export default QueryPageWithApollo;
